// Global Instructions Rule Applied!
//
// Configuration → Internal Agents admin models.
//
// Shape mirrors public.view__internal_agents_admin and
// public.view__internal_agents_usage. Four editable collections:
//   * app_config   — the four global budget knobs (cfg.app_config group
//                    'internal_agents')
//   * agent_types  — per-type caps + kill switch (cfg.internal_agent_types)
//   * task_models  — (agent type, task) → model routing
//   * llm_models   — the provider/model registry
//
// The usage payload is display-only derived numbers with no edit path, so it is
// typed rather than Zod-validated: a new breakdown column would otherwise be a
// three-file change for no safety gained, and a .strict() failure there would
// blank a panel over a number nobody edits.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

/** One global budget knob. The RPC emits all four keys whether or not a cfg row
 *  exists, so an unseeded key renders as "code default" rather than a blank. */
export const InternalAgentAppConfigEntrySchema = z
  .object({
    // null when the key has never been seeded
    app_config__id: z.string().nullish(),
    app_config__config_key: z.string(),
    // jsonb: boolean for `enabled`, number for the three numeric knobs
    app_config__value_json: z.any(),
    app_config__environment: z.string().nullish(),
    app_config__is_environment_override: z.boolean().nullish(),
    app_config__exists: z.boolean().nullish(),
    app_config__description: z.string().nullish(),
    app_config__date_modified: z.string().nullish()
  })
  .strict()

export type InternalAgentAppConfigEntry = z.infer<typeof InternalAgentAppConfigEntrySchema>

export const InternalAgentTypeAdminRowSchema = z
  .object({
    internal_agent_types__id: z.string(),
    internal_agent_types__agent_type_key: z.string(),
    internal_agent_types__name: z.string().nullish(),
    internal_agent_types__description: z.string().nullish(),
    internal_agent_types__subject_table: z.string().nullish(),
    internal_agent_types__enabled: z.boolean().nullish(),
    internal_agent_types__max_cost_per_run_usd: z.number().nullish(),
    internal_agent_types__max_runs_per_day: z.number().nullish(),
    internal_agent_types__memory_token_budget: z.number().nullish(),
    // Follow lifecycle (design §7.5). auto_provision_role is READ-ONLY — the
    // role the provisioning trigger keys on; null means this type does not
    // auto-provision, which makes the three knobs below inert (the panel
    // renders them as "—" rather than as controls that do nothing).
    internal_agent_types__auto_provision_role: z.string().nullish(),
    internal_agent_types__auto_backfill_on_provision: z.boolean().nullish(),
    internal_agent_types__auto_backfill_max_per_hour: z.number().nullish(),
    // null = follow forever.
    internal_agent_types__unfollow_lag_days: z.number().nullish(),
    // cfg.pipeline_statuses ids excluded from pipeline membership; null = no filter.
    internal_agent_types__follow_exclude_decision_statuses: z.array(z.string()).nullish(),
    // Which membership selector the Fund backfill scope resolves through
    // (Phase 9): 'fund_companies' (portfolio companies) | 'fund_lps' (the
    // fund's investors) | null (= fund_companies). READ-ONLY in the panel.
    internal_agent_types__backfill_fund_selector: z.string().nullish(),
    // Schedule (internal_agents__enqueue_due). A null anchor is a real value:
    // the enqueue falls back to the type's date_created, so the schedule lands
    // on whatever weekday the cfg seed ran. scheduled_run_mode is READ-ONLY —
    // it must name a run mode the handler implements, so the panel shows it as
    // a label rather than a control.
    internal_agent_types__schedule_frequency_unit: z.string().nullish(),
    internal_agent_types__schedule_interval_count: z.number().nullish(),
    internal_agent_types__schedule_anchor_date: z.string().nullish(),
    internal_agent_types__scheduled_run_mode: z.string().nullish(),
    // Interaction debounce (minutes an interaction-triggered run waits for the
    // burst to settle before it is due). NULL = the enqueue's default of 30.
    internal_agent_types__debounce_minutes: z.number().nullish(),
    // Backfill depth policy (internal_agents__get_backfill_window, design
    // §8.A): a first-time follower reads the newest `max_interactions` inside
    // a lookback of `frequent` months (≥ threshold interactions/month over the
    // last year) or `sparse` months (null = unbounded). Editable since
    // 20261122120100.
    internal_agent_types__backfill_lookback_months_frequent: z.number().nullish(),
    internal_agent_types__backfill_lookback_months_sparse: z.number().nullish(),
    internal_agent_types__backfill_max_interactions: z.number().nullish(),
    internal_agent_types__backfill_frequency_threshold_per_month: z.number().nullish(),
    // Live instances of this type, and how many tasks are routed for it.
    prog__instance_count: z.number().nullish(),
    prog__task_model_count: z.number().nullish()
  })
  .strict()

export type InternalAgentTypeAdminRow = z.infer<typeof InternalAgentTypeAdminRowSchema>

export const InternalAgentTaskModelAdminRowSchema = z
  .object({
    internal_agent_task_models__id: z.string(),
    internal_agent_types__id: z.string(),
    internal_agent_types__agent_type_key: z.string().nullish(),
    internal_agent_types__name: z.string().nullish(),
    internal_agent_task_models__task_key: z.string(),
    internal_agent_task_models__llm_model_key: z.string(),
    // The per-task call options bag (effort, max_output_tokens, …).
    internal_agent_task_models__params: z.any(),
    internal_agent_task_models__enabled: z.boolean().nullish(),
    // false = this row names a model that is missing or disabled for this
    // environment. resolve_task() would raise LLMConfigError and park the run
    // 'unconfigured', so the panel warns rather than letting it fail silently.
    prog__model_is_live: z.boolean().nullish(),
    // Drives which params the editor offers (effort → Anthropic, reasoning → OpenAI).
    prog__model_provider: z.string().nullish(),
    prog__model_name: z.string().nullish()
  })
  .strict()

export type InternalAgentTaskModelAdminRow = z.infer<typeof InternalAgentTaskModelAdminRowSchema>

export const LlmModelAdminRowSchema = z
  .object({
    llm_models__id: z.string(),
    llm_models__model_key: z.string(),
    llm_models__provider: z.string(),
    // The provider's API model id, e.g. 'claude-opus-5'.
    llm_models__model_name: z.string(),
    // Names the BACKEND env var holding the key — the key never enters the DB.
    llm_models__api_key_secret_name: z.string(),
    llm_models__input_cost_per_mtok: z.number().nullish(),
    llm_models__output_cost_per_mtok: z.number().nullish(),
    llm_models__max_output_tokens_default: z.number().nullish(),
    llm_models__enabled: z.boolean().nullish(),
    llm_models__description: z.string().nullish(),
    llm_models__environment: z.string().nullish(),
    llm_models__is_environment_override: z.boolean().nullish(),
    // false = a live row shadowed by an environment-specific one. Shown so a
    // row that cannot take effect reads as shadowed, not as broken.
    llm_models__is_effective: z.boolean().nullish(),
    // The in-use guard source: live routing rows naming this model_key.
    prog__task_model_count: z.number().nullish()
  })
  .strict()

export type LlmModelAdminRow = z.infer<typeof LlmModelAdminRowSchema>

/** One editable jsonb key: the write whitelist and the editor's input spec,
 *  both from cfg.agent_config_value_keys. */
export const AgentConfigValueKeySchema = z
  .object({
    agent_config_value_keys__value_key: z.string(),
    agent_config_value_keys__label: z.string(),
    agent_config_value_keys__value_type: z.string(),
    agent_config_value_keys__min_value: z.number().nullish(),
    agent_config_value_keys__max_value: z.number().nullish(),
    agent_config_value_keys__allowed_values: z.any(),
    agent_config_value_keys__applies_to_kind: z.string().nullish(),
    agent_config_value_keys__applies_to_provider: z.string().nullish(),
    agent_config_value_keys__description: z.string().nullish()
  })
  .strict()

export type AgentConfigValueKey = z.infer<typeof AgentConfigValueKeySchema>

export function validateInternalAgentAppConfigEntry(
  data: unknown,
  options?: { isArray?: boolean }
): InternalAgentAppConfigEntry | InternalAgentAppConfigEntry[] {
  try {
    return validateWithZodSchema(
      InternalAgentAppConfigEntrySchema,
      data,
      'internal agent app config entry',
      options
    ) as InternalAgentAppConfigEntry | InternalAgentAppConfigEntry[]
  } catch (e) {
    throw new Error(`Internal agent budget setting validation failed: ${e}`)
  }
}

/** One LP status's cadence policy (cfg.comms_lp_statuses, UAT follow-ups
 *  PR C). rm_touchpoint_due_days null = this status is never chased by
 *  cadence; draft mode 'signal' = a due touchpoint raises the chip only. */
export const LpStatusCadenceRowSchema = z
  .object({
    comms_lp_statuses__id: z.string(),
    comms_lp_statuses__label: z.string(),
    comms_lp_statuses__precedence: z.number().nullish(),
    comms_lp_statuses__rm_touchpoint_due_days: z.number().nullish(),
    // cfg.comms_lp_statuses.rm_cadence_draft_mode CHECK (draft | signal)
    comms_lp_statuses__rm_cadence_draft_mode: z.enum(['draft', 'signal']),
    comms_lp_statuses__agent_priority: z.number()
  })
  .strict()

export type LpStatusCadenceRow = z.infer<typeof LpStatusCadenceRowSchema>

export function validateLpStatusCadenceRow(
  data: unknown,
  options?: { isArray?: boolean }
): LpStatusCadenceRow | LpStatusCadenceRow[] {
  try {
    return validateWithZodSchema(
      LpStatusCadenceRowSchema,
      data,
      'LP status cadence row',
      options
    ) as LpStatusCadenceRow | LpStatusCadenceRow[]
  } catch (e) {
    throw new Error(`LP status cadence validation failed: ${e}`)
  }
}

export function validateInternalAgentTypeAdminRow(
  data: unknown,
  options?: { isArray?: boolean }
): InternalAgentTypeAdminRow | InternalAgentTypeAdminRow[] {
  try {
    return validateWithZodSchema(
      InternalAgentTypeAdminRowSchema,
      data,
      'internal agent type admin row',
      options
    ) as InternalAgentTypeAdminRow | InternalAgentTypeAdminRow[]
  } catch (e) {
    throw new Error(`Internal agent type validation failed: ${e}`)
  }
}

export function validateInternalAgentTaskModelAdminRow(
  data: unknown,
  options?: { isArray?: boolean }
): InternalAgentTaskModelAdminRow | InternalAgentTaskModelAdminRow[] {
  try {
    return validateWithZodSchema(
      InternalAgentTaskModelAdminRowSchema,
      data,
      'internal agent task model admin row',
      options
    ) as InternalAgentTaskModelAdminRow | InternalAgentTaskModelAdminRow[]
  } catch (e) {
    throw new Error(`Task routing validation failed: ${e}`)
  }
}

export function validateLlmModelAdminRow(
  data: unknown,
  options?: { isArray?: boolean }
): LlmModelAdminRow | LlmModelAdminRow[] {
  try {
    return validateWithZodSchema(
      LlmModelAdminRowSchema,
      data,
      'llm model admin row',
      options
    ) as LlmModelAdminRow | LlmModelAdminRow[]
  } catch (e) {
    throw new Error(`Model registry validation failed: ${e}`)
  }
}

/** Usage totals since UTC midnight — the same boundary the daily cap uses. */
export interface InternalAgentsUsageTotals {
  prog__cost_usd_today?: number
  prog__input_tokens_today?: number
  prog__output_tokens_today?: number
  prog__llm_calls_today?: number
  prog__runs_today?: number
  prog__runs_failed_today?: number
}

export interface InternalAgentsUsageByType {
  internal_agent_types__agent_type_key?: string
  internal_agent_types__name?: string
  prog__cost_usd_today?: number
  prog__llm_calls_today?: number
  prog__runs_today?: number
  internal_agent_types__max_runs_per_day?: number
}

export interface InternalAgentsUsageByModel {
  agent_run_llm_calls__llm_model_key?: string
  agent_run_llm_calls__provider?: string
  agent_run_llm_calls__model_name?: string
  prog__cost_usd_today?: number
  prog__input_tokens_today?: number
  prog__output_tokens_today?: number
  prog__call_count_today?: number
}

export interface InternalAgentsUsageByRunMode {
  agent_runs__run_mode?: string
  prog__runs?: number
  prog__runs_failed?: number
  prog__llm_calls?: number
  prog__cost_usd?: number
}

export interface InternalAgentsUsageBySubject {
  agent_instances__id?: string
  internal_agent_types__agent_type_key?: string
  agent_instances__subject_table?: string
  agent_instances__subject_id?: string
  subject_name?: string | null
  prog__cost_usd?: number
  prog__llm_calls?: number
  prog__runs?: number
}

export interface InternalAgentsUsageByDay {
  prog__day?: string
  prog__cost_usd?: number
  prog__llm_calls?: number
  prog__runs?: number
}

export interface InternalAgentsUsageData {
  since_utc?: string
  /** 1 = since UTC midnight (the daily-cap boundary); wider = the Phase 5 cost report window. */
  window_days?: number
  by_run_mode?: InternalAgentsUsageByRunMode[]
  by_subject?: InternalAgentsUsageBySubject[]
  by_day?: InternalAgentsUsageByDay[]
  totals: InternalAgentsUsageTotals
  by_agent_type: InternalAgentsUsageByType[]
  by_model: InternalAgentsUsageByModel[]
  by_agent_type_task: Array<{
    internal_agent_types__agent_type_key?: string
    agent_run_llm_calls__task_key?: string
    agent_run_llm_calls__llm_model_key?: string
    prog__cost_usd_today?: number
    prog__call_count_today?: number
  }>
}

export interface InternalAgentsConfigAdminData {
  /** Which environment layer the caps resolved from ('global' unless an
   *  environment-specific row shadows it). */
  resolvedEnvironment: string
  appConfig: InternalAgentAppConfigEntry[]
  lpStatusCadence: LpStatusCadenceRow[]
  agentTypes: InternalAgentTypeAdminRow[]
  taskModels: InternalAgentTaskModelAdminRow[]
  models: LlmModelAdminRow[]
  taskKeys: string[]
  providers: string[]
  taskParamKeys: AgentConfigValueKey[]
}
